// External-link opening for the MCP App surface (e.g. Pix Pulse embedded in
// ChatGPT). The host renders the app in a sandboxed iframe without
// `allow-popups`, so a direct `window.open` for a product/retailer link is
// blocked. On the `mcp-app` surface we instead ask the MCP App shell to open
// the URL through the host (it forwards `ui/open-link` to ChatGPT); the shell
// origin is the `pt` param the shell appended to our embed URL. On every other
// surface (publisher embed, standalone) we open the window directly, as before.

import type { McpAppMessage, ThemeName } from "@/types/messages";

const MCP_APP_SURFACE = "mcp-app";

// Returns the shell's origin to post to when we're inside the MCP App surface,
// or null when we're not (so callers fall back to window.open).
function mcpAppParentOrigin(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pix_surface") !== MCP_APP_SURFACE) {
      return null;
    }
    const parentOrigin = params.get("pt");
    if (!parentOrigin || window.parent === window) {
      return null;
    }
    return parentOrigin;
  } catch {
    return null;
  }
}

// Reports the player's current content height to the MCP App shell so it can
// size the card to fit (instead of a fixed height that clips the tabbed layout).
// No-op off the MCP surface (mcpAppParentOrigin() returns null). The shell only
// honours this on desktop-width cards; the mobile fallback keeps its fixed size.
export function postCardHeight(height: number) {
  const parentOrigin = mcpAppParentOrigin();
  if (!parentOrigin || !height || height <= 0) {
    return;
  }
  try {
    window.parent.postMessage(
      {
        pixmotoMcpApp: true,
        type: "resize",
        height: Math.ceil(height),
      } satisfies McpAppMessage,
      parentOrigin,
    );
  } catch {
    // malformed targetOrigin — ignore
  }
}

// Subscribes to live theme changes posted by the MCP App shell — the viewer
// toggled the host's light/dark theme mid-session. Calls
// `cb('light' | 'dark')` on each change. Returns an unsubscribe function; a
// no-op off the MCP surface. Only messages from the shell origin (the `pt`
// param) carrying our envelope are honoured.
export function onMcpThemeChange(cb: (theme: ThemeName) => void): () => void {
  const parentOrigin = mcpAppParentOrigin();
  if (!parentOrigin || typeof window === "undefined") {
    return function () {
      /* no-op */
    };
  }
  function handler(event: MessageEvent) {
    if (event.origin !== parentOrigin) {
      return;
    }
    const d: (Partial<McpAppMessage> & { theme?: unknown }) | null = event.data;
    if (!d || d.pixmotoMcpApp !== true || d.type !== "theme") {
      return;
    }
    if (d.theme === "light" || d.theme === "dark") {
      cb(d.theme);
    }
  }
  window.addEventListener("message", handler);
  return function () {
    window.removeEventListener("message", handler);
  };
}

// Opens an external URL. Inside the MCP App shell this hands the URL to the host
// via the shell; everywhere else it behaves exactly like window.open.
export function openExternalUrl(
  url: string | null | undefined,
  windowName?: string,
  options?: { noopener?: boolean },
) {
  if (!url) {
    return;
  }
  const parentOrigin = mcpAppParentOrigin();
  if (parentOrigin) {
    try {
      // postMessage throws synchronously on a malformed targetOrigin; guard so a
      // bad `pt` can't break the caller (and skip its analytics.track). Falls
      // back to window.open below on failure.
      window.parent.postMessage(
        {
          pixmotoMcpApp: true,
          type: "open-link",
          url: url,
        } satisfies McpAppMessage,
        parentOrigin,
      );
      return;
    } catch {
      // fall through to window.open
    }
  }
  // `noopener` is OPT-IN (`options.noopener`) because it also disables named-window
  // reuse — a same-name `window.open` no longer finds an existing tab, so it always
  // opens a fresh one. Only the retailer path opts in: it already wants a new tab
  // per link AND benefits from severing `window.opener` so the external
  // retailer site can't reverse-tab-nab us. The per-item/per-experience callers pass
  // no options, keeping their named-window reuse (repeat clicks reuse the same tab).
  // Deliberately NOT `noreferrer` — retailer affiliate attribution needs the Referer.
  const features = options && options.noopener ? "noopener" : undefined;
  window.open(url, windowName, features);
}
